import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { ArrowUpRight, Sprout, FlaskConical, Mountain, Heart, Leaf, Wheat, Sun, CalendarDays } from "lucide-react";

export default function Landing() {
  const [packages, setPackages] = useState([]);
  const location = useLocation();

  useEffect(() => {
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (location.hash === "#caring-for-country") {
      const el = document.getElementById("caring-for-country");
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 120);
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-[#F9F6F0]">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="px-6 sm:px-12 lg:px-20 pt-20 lg:pt-28 pb-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 relative z-10">
            <p className="label-eyebrow mb-6 fade-up" data-testid="hero-eyebrow">
              Vitality · Longevity · Ancestral Science
            </p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight text-[#2D322B] leading-[1.05] fade-up-delay-1">
              Where the lab bench
              <span className="block italic text-[#C05C42]">meets the medicine garden.</span>
            </h1>
            <p className="mt-8 text-lg text-[#5C6359] leading-relaxed max-w-xl fade-up-delay-2">
              Badi is a membership for those tending the long arc of their vitality —
              weaving precision medicine, herbal & plant medicine, and Indigenous science into
              one living practice.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 fade-up-delay-3">
              <Link to="/signup" data-testid="hero-cta-join" className="btn-primary inline-flex items-center gap-2">
                Begin your membership <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link to="/contact" data-testid="hero-cta-book" className="inline-flex items-center gap-2 rounded-full px-8 py-3 text-sm font-medium tracking-wide uppercase bg-[#D49A44] text-[#2D322B] hover:bg-[#bf8a3a] transition-colors">
                <CalendarDays className="w-4 h-4" /> Contact us
              </Link>
              <Link to="/library" data-testid="hero-cta-explore" className="btn-secondary">
                Explore the library
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="absolute -top-6 -left-6 w-32 h-32 rounded-full bg-[#D49A44]/30 blur-3xl"></div>
            <div className="relative rounded-3xl overflow-hidden border border-[#D1C7B8]/60 shadow-[0_20px_80px_-30px_rgba(42,59,44,0.35)]">
              <img
                src="https://images.unsplash.com/photo-1724770646663-2a806ed04ca4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxpbmRpZ2Vub3VzJTIwZm9yZXN0JTIwbGFuZHNjYXBlJTIwc3VubGlnaHR8ZW58MHx8fHwxNzgxOTYyODQyfDA&ixlib=rb-4.1.0&q=85"
                alt="Sunlit ancient forest"
                className="w-full h-[520px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2D322B]/45 via-transparent to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6 text-[#F9F6F0]">
                <p className="label-eyebrow !text-[#F9F6F0]/80">Member Practice</p>
                <p className="font-serif text-2xl mt-1">Mitochondrial restoration · week 4</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="px-6 sm:px-12 lg:px-20 py-24 bg-[#F1ECE4] border-y border-[#D1C7B8]/60">
        <div className="max-w-6xl mx-auto">
          <p className="label-eyebrow mb-4">Four lineages, one fire</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#2D322B] mb-16 max-w-3xl">
            Badi is built on four interlacing roots.
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <FlaskConical className="w-6 h-6" />, title: "Precision Medicine", body: "Biomarkers, epigenetics, lipidomics — reading the body's instrumentation." },
              { icon: <Sprout className="w-6 h-6" />, title: "Plant & Herbal", body: "From ashwagandha to reishi to maca — the green pharmacy in its full intelligence." },
              { icon: <Mountain className="w-6 h-6" />, title: "Indigenous Science", body: "Knowledge keepers from First Nations, Andean, Amazonian, and Ayurvedic lineages." },
              { icon: <Heart className="w-6 h-6" />, title: "Living Vitality", body: "Cold, breath, sleep, sun, ceremony — the daily acts that compound into a long life." },
            ].map((p, i) => (
              <div key={p.title} className="bg-[#F9F6F0] border border-[#D1C7B8] rounded-2xl p-8 hover:-translate-y-1 transition-transform duration-300" data-testid={`pillar-${i}`}>
                <div className="w-12 h-12 rounded-full bg-[#C05C42]/15 text-[#C05C42] flex items-center justify-center mb-6">
                  {p.icon}
                </div>
                <h3 className="font-serif text-2xl text-[#2D322B] mb-3">{p.title}</h3>
                <p className="text-sm text-[#5C6359] leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CARING FOR COUNTRY */}
      <section id="caring-for-country" className="px-6 sm:px-12 lg:px-20 py-24" data-testid="caring-for-country">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 relative order-2 lg:order-1">
              <div className="absolute -bottom-6 -right-6 w-40 h-40 rounded-full bg-[#3F6B45]/25 blur-3xl"></div>
              <div className="relative rounded-3xl overflow-hidden border border-[#D1C7B8]/60 shadow-[0_20px_80px_-30px_rgba(42,59,44,0.4)]">
                <img
                  src="https://images.unsplash.com/photo-1624295887442-f5791f74e6c1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                  alt="Regenerative permaculture garden"
                  className="w-full h-[460px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2D322B]/55 via-transparent to-transparent"></div>
                <div className="absolute bottom-6 left-6 right-6 text-[#F9F6F0]">
                  <p className="label-eyebrow !text-[#F9F6F0]/80">From the ground up</p>
                  <p className="font-serif text-2xl mt-1">Healthy soil → healthy gut → long life</p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-6 order-1 lg:order-2">
              <p className="label-eyebrow mb-4 !text-[#3F6B45]">Caring for Country</p>
              <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#2D322B] leading-[1.08]">
                Grow your own
                <span className="block italic text-[#C05C42]">medicine.</span>
              </h2>
              <p className="mt-6 text-base text-[#5C6359] leading-relaxed max-w-xl">
                For 60,000 years, the oldest living cultures tended Country and body as one system.
                Badi carries that thread forward — guiding members to regenerate soil, grow nutrient-dense
                food, lower their toxin load, and eat the way longevity is actually built: from the ground up.
              </p>

              <div className="mt-8 space-y-4">
                {[
                  { icon: <Leaf className="w-5 h-5" />, title: "Regenerative agroecology", body: "Build living, microbially-rich soil — and grow your own food off-grid, the ancestral way." },
                  { icon: <Wheat className="w-5 h-5" />, title: "Food is medicine", body: "Australian native foods — gumbi gumbi, Kakadu plum, wattleseed — as everyday plant medicine." },
                  { icon: <Sun className="w-5 h-5" />, title: "The longevity link", body: "Cleaner soil and a richer gut microbiome are preventative medicine at the source." },
                ].map((f, i) => (
                  <div key={f.title} className="flex gap-4" data-testid={`country-point-${i}`}>
                    <div className="shrink-0 w-10 h-10 rounded-full bg-[#3F6B45]/12 text-[#3F6B45] flex items-center justify-center mt-0.5">
                      {f.icon}
                    </div>
                    <div>
                      <h3 className="font-serif text-xl text-[#2D322B]">{f.title}</h3>
                      <p className="text-sm text-[#5C6359] leading-relaxed mt-1">{f.body}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/pricing?plan=vital" data-testid="country-cta-trial" className="btn-primary inline-flex items-center gap-2">
                  Start with Vital <ArrowUpRight className="w-4 h-4" />
                </Link>
                <Link to="/caring-for-country" data-testid="country-cta-read" className="btn-secondary">
                  Explore Caring for Country
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MEMBERSHIP TEASE */}
      <section className="px-6 sm:px-12 lg:px-20 py-24">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="label-eyebrow mb-4">Membership</p>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#2D322B] mb-6">
              Three paths inward.
            </h2>
            <p className="text-base text-[#5C6359] leading-relaxed mb-8">
              Each tier is a deeper invitation into the practice — from foundational essays to
              one-on-one practitioner introductions and live elder circles.
            </p>
            <Link to="/pricing" className="btn-primary inline-flex items-center gap-2" data-testid="see-membership-btn">
              See all tiers <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="lg:col-span-7 grid md:grid-cols-3 gap-4">
            {packages.map((p) => (
              <div key={p.id} className="bg-[#F1ECE4] border border-[#D1C7B8] rounded-2xl p-6 flex flex-col" data-testid={`landing-pkg-${p.id}`}>
                <p className="label-eyebrow">{p.name}</p>
                <p className="font-serif text-4xl text-[#2D322B] mt-3">${p.price_monthly}<span className="text-base text-[#5C6359]">/mo</span></p>
                <p className="text-xs text-[#5C6359] mt-3 leading-relaxed flex-1">{p.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* QUOTE / BAND */}
      <section className="px-6 sm:px-12 lg:px-20 py-20 bg-[#2D322B] text-[#F9F6F0]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="label-eyebrow !text-[#D49A44] mb-6">Haudenosaunee teaching</p>
          <p className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-snug italic">
            “In every deliberation, we must consider the impact on the seventh generation.”
          </p>
          <p className="mt-8 text-sm tracking-widest uppercase text-[#F9F6F0]/70">— The Great Law of Peace</p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
